import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Star } from "lucide-react";
import { CATEGORIES, PROJECTS, PROJECT_ROUTES, DELIVERABLES } from "../data/projects";
import { TechTag, FadeIn, slug } from "./common";
import CategoryHeader from "./CategoryHeader";
import { CategoryBackground } from "./SciBackground";
import { useView } from "../context/ViewContext";

export default function ProjectExplorer({ recruiterLimit = 0 }) {
  const [category, setCategory] = useState("All");
  const { isRecruiter } = useView();
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    let list = category === "All" ? PROJECTS : PROJECTS.filter((p) => p.categories.includes(category));
    if (recruiterLimit) {
      list = [...PROJECTS].sort((a, b) => (b.flagship ? 1 : 0) - (a.flagship ? 1 : 0)).slice(0, recruiterLimit);
    }
    return list;
  }, [category, recruiterLimit]);

  return (
    <section id="projects" className="relative overflow-hidden scroll-mt-16" data-testid="project-explorer">
      <CategoryBackground variant="blueprint" accent="eblue" accent2="cyan" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <CategoryHeader
        id="projects"
        category="projects"
        eyebrow="Project Explorer"
        title={isRecruiter ? "Strongest representative work" : "Case studies in manufacturing problem-solving"}
        description={
          isRecruiter
            ? "Four flagship projects. Switch to Technical View for the full explorer and complete case studies."
            : "A connected engineering portfolio — eight projects across RCA, automation, analytics, IoT, Quality Systems and continuous improvement. Every case study follows the same discipline: business problem → evidence → method → solution → impact."
        }
      />
      {!recruiterLimit && (
        <div className="flex flex-wrap gap-2 mt-8" data-testid="project-category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`project-filter-${slug(c)}`}
              onClick={() => setCategory(c)}
              className={`font-mono text-[11px] px-3 py-1.5 rounded-sm border transition-colors ${
                category === c ? "bg-cyan text-navy border-cyan" : "border-teal/40 text-pale/70 hover:border-cyan/60 hover:text-paper"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {filtered.map((p, i) => (
          <FadeIn key={p.id} delay={(i % 3) * 0.06} className={p.flagship ? "md:col-span-2 lg:col-span-1" : ""}>
            <button
              data-testid={`project-card-${p.id}`}
              onClick={() => navigate(PROJECT_ROUTES[p.id].slug)}
              className="group w-full h-full text-left panel p-6 hover:border-cyan/50 transition-colors flex flex-col"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                  {p.categories.slice(0, 2).map((c) => (
                    <span key={c} className="font-mono text-[9px] uppercase tracking-wider text-cyan/80">
                      {c}
                    </span>
                  ))}
                </div>
                {p.flagship && (
                  <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-warn">
                    <Star className="w-3 h-3" aria-hidden="true" /> Flagship
                  </span>
                )}
              </div>
              <h3 className="font-sans text-lg font-semibold tracking-tight text-paper mt-3 group-hover:text-cyan transition-colors">
                {p.title}
              </h3>
              <div className="mt-3 space-y-2 flex-1">
                <p className="text-xs text-steel leading-relaxed line-clamp-3">
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-teal block mb-0.5">Problem</span>
                  {p.problem}
                </p>
                <p className="text-xs text-steel leading-relaxed line-clamp-2">
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-teal block mb-0.5">Method</span>
                  {p.methodology}
                </p>
                <p className="text-xs text-pale/75 leading-relaxed line-clamp-2">
                  <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-teal block mb-0.5">Deliverable</span>
                  {DELIVERABLES[p.id]}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {p.tags.slice(0, 5).map((t) => (
                  <TechTag key={t}>{t}</TechTag>
                ))}
              </div>
              <div className="flex items-center gap-1.5 mt-5 font-mono text-[11px] text-cyan">
                Open case study
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
              </div>
            </button>
          </FadeIn>
        ))}
      </div>
      </div>
    </section>
  );
}
